import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import useGet from 'lib/hooks/useGet';
import { TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS } from 'lib/networking/endpoints';
import {
  constructUrlPathSegment,
  getCategoryIDFromUrl,
  getCategoryNameFromUrl,
} from 'components/upb_viewer/UPBViewer';
import BetaBreadcrumb from 'components/upb_viewer_beta/BetaBreadcrumb';
import BetaCategoryGrid from 'components/upb_viewer_beta/BetaCategoryGrid';
import BetaPaneSelect from 'components/upb_viewer_beta/BetaPaneSelect';
import useUPBBetaChildCategoryStats from 'components/upb_viewer_beta/hooks/useUPBBetaChildCategoryStats';
import {
  BG_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_H1,
  FONT_SIZE_SMALL,
  FONT_TITLE,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_EXTRABOLD,
  FONT_WEIGHT_MEDIUM,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaCategoryPage — category drill-down for the UPB Viewer Beta.
 *
 * Given the URL (l1[/l2[/l3]]), fetches the immediate child categories from
 * the taxonomy endpoint plus per-child stats (annualized GMV, active SKUs, DP
 * count) and renders them in a beta-styled card grid that matches the design
 * mockup. Clicking a card appends its segment to the URL; when the URL reaches
 * L4, the container routes to BetaProductsPage.
 */
function BetaCategoryPage({
  urlParams,
  onNavigate,
  baseURL,
  vendorId,
  thirdPartyTag,
  skuType,
  skuTypeOptions,
  onSkuTypeChange,
}) {
  const { l1, l2, l3 } = urlParams;
  const l1Id = getCategoryIDFromUrl(l1);
  const l2Id = getCategoryIDFromUrl(l2);
  const l3Id = getCategoryIDFromUrl(l3);

  const { data: { data: categories } = {}, loading } = useGet(
    TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS,
    {
      l1_category_id: l1Id || null,
      l2_category_id: l2Id || null,
      l3_category_id: l3Id || null,
      is_upb: true,
    },
  );

  const { data: { data: statsRows = [] } = {}, loading: statsLoading } =
    useUPBBetaChildCategoryStats({
      l1CategoryId: l1Id || undefined,
      l2CategoryId: l2Id || undefined,
      l3CategoryId: l3Id || undefined,
      vendorId: vendorId || undefined,
      thirdPartyTag: thirdPartyTag || undefined,
    });

  const statsById = useMemo(() => {
    const map = {};
    statsRows.forEach(row => {
      if (row && row.child_id != null) {
        map[Number(row.child_id)] = row;
      }
    });
    return map;
  }, [statsRows]);

  // Which level do the returned children represent? (one below the deepest filter)
  let childLevelLabel = 'L2';
  if (l3Id) childLevelLabel = 'L4';
  else if (l2Id) childLevelLabel = 'L3';

  // Tag pill describing the level of the *current* node (= the deepest filter).
  let currentLevelLabel = null;
  if (l3) currentLevelLabel = 'L3';
  else if (l2) currentLevelLabel = 'L2';
  else if (l1) currentLevelLabel = 'L1';

  // Breadcrumb crumbs — last is current (no url). Level tags ('L1', 'L2', 'L3')
  // are attached to each category crumb to match the design mockup. Single
  // root crumb ("All Categories") — used to be Home + All Categories pointing
  // at the same URL, which was confusing (Devin review).
  const crumbs = [{ label: 'All Categories', url: `/${baseURL}` }];
  if (l1) {
    crumbs.push({
      label: getCategoryNameFromUrl(l1) || 'Category',
      url: l2 || l3 ? `/${baseURL}/${l1}` : undefined,
      tag: 'L1',
    });
  }
  if (l2) {
    crumbs.push({
      label: getCategoryNameFromUrl(l2) || 'Category',
      url: l3 ? `/${baseURL}/${l1}/${l2}` : undefined,
      tag: 'L2',
    });
  }
  if (l3) {
    crumbs.push({
      label: getCategoryNameFromUrl(l3) || 'Category',
      tag: 'L3',
    });
  }

  const currentTitle = crumbs[crumbs.length - 1]?.label || 'Categories';
  const childCount = categories?.length || 0;

  const handleCardClick = child => {
    if (!onNavigate) return;
    const childSeg = constructUrlPathSegment({
      value: child.id,
      label: child.taxonomy_display_name || child.category_name || '',
    });
    const parts = [baseURL, l1, l2, l3, childSeg].filter(Boolean);
    onNavigate(`/${parts.join('/')}`);
  };

  return (
    <div className={css(styles.page)}>
      <BetaBreadcrumb crumbs={crumbs} onNavigate={onNavigate} />
      <div className={css(styles.sectionHead)}>
        <h2 className={css(styles.title)}>{currentTitle}</h2>
        {currentLevelLabel && <span className={css(styles.levelTag)}>{currentLevelLabel}</span>}
        <span className={css(styles.count)}>
          {childCount === 0
            ? 'No items'
            : `${childCount} ${childLevelLabel} ${childCount === 1 ? 'item' : 'items'}`}
        </span>
        {onSkuTypeChange && skuTypeOptions && skuTypeOptions.length > 0 && (
          <BetaPaneSelect
            caption="SKU type"
            options={skuTypeOptions}
            value={skuType}
            onChange={onSkuTypeChange}
            menuWidth={240}
            minWidth={170}
            compact
          />
        )}
      </div>

      <BetaCategoryGrid
        categories={categories || []}
        statsById={statsById}
        loading={loading || statsLoading}
        onCardClick={handleCardClick}
        skuType={skuType}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
  },

  title: {
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_EXTRABOLD,
    fontSize: FONT_SIZE_H1,
    color: FG_1,
    letterSpacing: '-0.01em',
    margin: 0,
    textTransform: 'capitalize',
  },

  levelTag: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 10,
    color: FG_3,
    backgroundColor: BG_2,
    padding: '3px 8px',
    borderRadius: 9999,
    letterSpacing: '0.04em',
  },

  count: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
    marginLeft: 'auto',
  },
});

BetaCategoryPage.propTypes = {
  urlParams: PropTypes.shape({
    l1: PropTypes.string,
    l2: PropTypes.string,
    l3: PropTypes.string,
    l4: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
  baseURL: PropTypes.string,
  vendorId: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  skuType: PropTypes.string,
  skuTypeOptions: PropTypes.arrayOf(PropTypes.object),
  onSkuTypeChange: PropTypes.func,
};

BetaCategoryPage.defaultProps = {
  baseURL: 'upb-viewer-beta',
  vendorId: null,
  thirdPartyTag: null,
  skuType: 'active',
  skuTypeOptions: null,
  onSkuTypeChange: null,
};

export default BetaCategoryPage;
